import React, { useEffect, useRef, useState } from 'react';
import GlyphSvg from './Glyphs';
import '../utils/animated-glyph-grid.css';

const GRID_SIZE = 6;
const CELL_COUNT = GRID_SIZE * GRID_SIZE;
const SELECTED_GLYPH = 'g02';
const DEFAULT_GLYPH = 'g01';
const SELECTION_COUNT = 16;
const CYCLE_DURATION = 3000; // ms
const STEP_DURATION = CYCLE_DURATION / SELECTION_COUNT;
const REVERT_DURATION = 250; // ms
const REVERT_STEP = REVERT_DURATION / SELECTION_COUNT;

function getRandomUniqueIndices(count, max) {
  const indices = new Set();
  while (indices.size < count) {
    indices.add(Math.floor(Math.random() * max));
  }
  return Array.from(indices);
}

export default function AnimatedGlyphGrid({ size = 18 }) {
  const [selected, setSelected] = useState([]); // indices currently g02
  const [reverting, setReverting] = useState([]); // indices still g02 during revert
  const selectionOrder = useRef([]);
  const step = useRef(0);
  const intervalRef = useRef();
  const cycleTimeoutRef = useRef();
  const revertTimeouts = useRef([]);

  useEffect(() => {
    function startCycle() {
      // Pick 8 unique indices in random order
      selectionOrder.current = getRandomUniqueIndices(SELECTION_COUNT, CELL_COUNT);
      step.current = 0;
      setSelected([]);
      setReverting([]);

      // Progressive selection
      intervalRef.current = setInterval(() => {
        step.current += 1;
        setSelected(selectionOrder.current.slice(0, step.current));
        if (step.current >= SELECTION_COUNT) {
          clearInterval(intervalRef.current);
        }
      }, STEP_DURATION);

      // End of cycle: after CYCLE_DURATION, start staggered revert
      cycleTimeoutRef.current = setTimeout(() => {
        clearInterval(intervalRef.current);
        setReverting(selectionOrder.current.slice()); // all selected start as reverting
        setSelected([]); // clear selected
        // Stagger revert
        revertTimeouts.current.forEach(t => clearTimeout(t));
        revertTimeouts.current = [];
        selectionOrder.current.forEach((idx, i) => {
          const t = setTimeout(() => {
            setReverting(prev => prev.filter(x => x !== idx));
          }, i * REVERT_STEP);
          revertTimeouts.current.push(t);
        });
        // After all reverted, start next cycle
        const totalRevert = REVERT_STEP * SELECTION_COUNT;
        const nextCycleTimeout = setTimeout(() => {
          setReverting([]);
          startCycle();
        }, totalRevert + 10);
        revertTimeouts.current.push(nextCycleTimeout);
      }, CYCLE_DURATION);
    }

    startCycle();
    return () => {
      clearInterval(intervalRef.current);
      clearTimeout(cycleTimeoutRef.current);
      revertTimeouts.current.forEach(t => clearTimeout(t));
    };
  }, []);

  // Cells are g02 if in selected or reverting
  const active = new Set([...selected, ...reverting]);

  return (
    <div className="animated-glyph-grid grid-6x6">
      {Array.from({ length: CELL_COUNT }).map((_, i) => (
        <div className="glyph-grid-cell" key={i}>
          <GlyphSvg glyph={active.has(i) ? SELECTED_GLYPH : DEFAULT_GLYPH} size={size} color="var(--c5)" />
        </div>
      ))}
    </div>
  );
} 